// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { HoleFeature } from './useHoleFeatures';

/**
 * One tee or green in the editor, whether it came from the server or was just clicked.
 *
 * `id` is what separates the two: a marker with one exists server-side and can be deleted
 * there, a marker without one has never left the browser.
 */
export type DraftMarker = {
  id: string | null;
  kind: MarkerKind;
  /**
   * Discriminates tee boxes on the hole, and turning points down it. Always 0 for the green.
   */
  ordinal: number;
  name: string | null;
  lat: number;
  lon: number;
  /** Where the stored position came from. `manual` once saved from here. */
  source: string;
};

/** Everything marked on one hole. */
export type HoleDraft = {
  holeNumber: number;
  markers: DraftMarker[];
};

/**
 * What can be marked on a hole.
 *
 * A `turn` is where the hole bends — one on a par 4, two on a par 5; see
 * {@link ./holeTurnPoints}. Only the ones an admin actually dragged are markers: a turning
 * point still sitting where the straight line put it is nothing anybody has stated, so it is
 * derived on the fly rather than stored.
 */
export type MarkerKind = 'tee' | 'turn' | 'green';

/** Tee end first, the way the hole is played. */
const KIND_ORDER: MarkerKind[] = ['tee', 'turn', 'green'];

/** A marker's identity inside a hole — the key the server upserts on. */
export type MarkerKey = string;

export const markerKey = (marker: Pick<DraftMarker, 'kind' | 'ordinal'>): MarkerKey =>
  `${marker.kind}:${marker.ordinal}`;

/** What the manual-save endpoint accepts. */
export type ManualFeaturePayload = {
  holeNumber: number;
  kind: MarkerKind;
  ordinal: number;
  name: string | null;
  lat: number;
  lon: number;
};

const byPosition = (marker: DraftMarker): string =>
  // Six decimals is ~0.1 m, finer than anything the editor can express by clicking, so two
  // positions that agree to here are the same position and the hole is not dirty.
  `${marker.lat.toFixed(6)},${marker.lon.toFixed(6)}`;

const sortMarkers = (markers: DraftMarker[]): DraftMarker[] =>
  [...markers].sort((a, b) =>
    a.kind === b.kind
      ? a.ordinal - b.ordinal
      : KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind)
  );

/** The editor's starting point: what the server currently holds for this hole. */
export const draftFromFeatures = (holeNumber: number, features: HoleFeature[]): HoleDraft => ({
  holeNumber,
  markers: sortMarkers(
    features
      .filter((feature) => feature.holeNumber === holeNumber)
      .map((feature) => ({
        id: feature.id,
        kind: feature.kind,
        ordinal: feature.ordinal,
        name: feature.name,
        lat: feature.lat,
        lon: feature.lon,
        source: feature.source,
      }))
  ),
});

/**
 * Adds a marker where the map was clicked.
 *
 * A hole has one green, so a second green click moves the one that is there rather than
 * adding another — clicking twice because the first landed in the wrong place is the common
 * case, and it must not leave two greens behind. Tees are the opposite: a hole legitimately
 * has several, so each click adds one.
 *
 * New tees take the next free ordinal and existing ones keep theirs. Ordinals are the key
 * the server upserts on, so renumbering after a delete would silently overwrite a different
 * tee box.
 */
export const placeMarker = (
  draft: HoleDraft,
  kind: 'tee' | 'green',
  point: { lat: number; lon: number },
  name: string | null = null
): HoleDraft => {
  if (kind === 'green') {
    const existing = draft.markers.find((marker) => marker.kind === 'green');
    if (existing) {
      return moveMarker(draft, markerKey(existing), point);
    }
  }

  const usedOrdinals = draft.markers
    .filter((marker) => marker.kind === kind)
    .map((marker) => marker.ordinal);
  const ordinal = kind === 'green' ? 0 : usedOrdinals.reduce((max, o) => Math.max(max, o + 1), 0);

  return {
    ...draft,
    markers: sortMarkers([
      ...draft.markers,
      { id: null, kind, ordinal, name, lat: point.lat, lon: point.lon, source: 'manual' },
    ]),
  };
};

/** Moves a marker that is already on the hole, keeping its identity and its server row. */
export const moveMarker = (
  draft: HoleDraft,
  key: MarkerKey,
  point: { lat: number; lon: number }
): HoleDraft => ({
  ...draft,
  markers: draft.markers.map((marker) =>
    markerKey(marker) === key ? { ...marker, lat: point.lat, lon: point.lon } : marker
  ),
});

/**
 * Fixes a turning point where it was dragged.
 *
 * Placing one is always a move, never an addition: how many turning points a hole has is
 * decided by its par, not by how often the map was clicked. `ordinal` is the point's index
 * down the hole, which is what makes "the first bend" the same row before and after a save.
 */
export const placeTurnPoint = (
  draft: HoleDraft,
  ordinal: number,
  point: { lat: number; lon: number }
): HoleDraft => {
  const existing = draft.markers.find(
    (marker) => marker.kind === 'turn' && marker.ordinal === ordinal
  );
  if (existing) return moveMarker(draft, markerKey(existing), point);

  return {
    ...draft,
    markers: sortMarkers([
      ...draft.markers,
      { id: null, kind: 'turn', ordinal, name: null, lat: point.lat, lon: point.lon, source: 'manual' },
    ]),
  };
};

/**
 * The turning points somebody has actually placed, by their index down the hole.
 *
 * `count` is what the hole's par allows, and anything beyond it is left out rather than kept
 * out of sight: a hole re-carded from a par 5 to a par 4 has one bend now, and the second
 * one's stored row has to leave with it — see {@link trimTurnPoints}.
 */
export const placedTurnPoints = (
  draft: HoleDraft,
  count: number
): Map<number, { lat: number; lon: number }> =>
  new Map(
    draft.markers
      .filter((marker) => marker.kind === 'turn' && marker.ordinal < count)
      .map((marker) => [marker.ordinal, { lat: marker.lat, lon: marker.lon }])
  );

/**
 * Drops turning points the hole's par no longer has room for, so the save deletes their
 * stored rows rather than leaving a bend nothing on screen accounts for.
 */
export const trimTurnPoints = (draft: HoleDraft, count: number): HoleDraft =>
  draft.markers.some((marker) => marker.kind === 'turn' && marker.ordinal >= count)
    ? {
        ...draft,
        markers: draft.markers.filter(
          (marker) => marker.kind !== 'turn' || marker.ordinal < count
        ),
      }
    : draft;

/** Renames a tee box. Greens have no name to give. */
export const renameMarker = (draft: HoleDraft, key: MarkerKey, name: string | null): HoleDraft => ({
  ...draft,
  markers: draft.markers.map((marker) =>
    markerKey(marker) === key ? { ...marker, name: name?.trim() || null } : marker
  ),
});

export const removeMarker = (draft: HoleDraft, key: MarkerKey): HoleDraft => ({
  ...draft,
  markers: draft.markers.filter((marker) => markerKey(marker) !== key),
});

/**
 * Whether the draft says anything the server does not already hold.
 *
 * Drives both the Save button and the warning on leaving a hole, so it has to notice a
 * marker added, moved, renamed or removed — and nothing else, or every hole would look
 * edited the moment it was opened.
 */
export const isDirty = (draft: HoleDraft, features: HoleFeature[]): boolean => {
  const saved = draftFromFeatures(draft.holeNumber, features);
  if (saved.markers.length !== draft.markers.length) return true;

  return draft.markers.some((marker) => {
    const match = saved.markers.find((other) => markerKey(other) === markerKey(marker));
    if (!match) return true;
    return byPosition(match) !== byPosition(marker) || (match.name ?? null) !== (marker.name ?? null);
  });
};

/**
 * The payload for one hole.
 *
 * The whole hole every time, unchanged markers included: the endpoint upserts on
 * (hole, kind, ordinal), so re-sending a marker that has not moved writes the same row it
 * already holds, and sending only the changes would make a partly failed save leave the
 * database in a state the editor cannot describe.
 */
export const toManualFeatures = (draft: HoleDraft): ManualFeaturePayload[] =>
  draft.markers.map((marker) => ({
    holeNumber: draft.holeNumber,
    kind: marker.kind,
    ordinal: marker.ordinal,
    name: marker.name,
    lat: marker.lat,
    lon: marker.lon,
  }));

/**
 * Server rows the draft no longer has a marker for.
 *
 * These need deleting explicitly. A save writes what it is given and never removes what it
 * is not, which is what keeps a save of hole 3 from touching hole 4.
 */
export const removedFeatureIds = (draft: HoleDraft, features: HoleFeature[]): string[] => {
  const kept = new Set(draft.markers.map((marker) => marker.id).filter((id): id is string => !!id));
  return features
    .filter((feature) => feature.holeNumber === draft.holeNumber && !kept.has(feature.id))
    .map((feature) => feature.id);
};
